import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Alert, Button, Input, Space, Spin, Steps } from 'antd'
import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useAuthSession } from '@/lib/session'
import { useToast } from '@/contexts/ToastContext'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { AdvisoryCallStatus } from '@/features/advisory-ai/types'
import {
  useAdvisoryAiReport,
  useConsolidatedAnalysis,
  useUpdateAdvisoryCallMutation,
} from '@/features/advisory-ai/hooks'
import { AgendaStep, DataStep, ReportStep, GenerateOutputsStep } from '../call/components'
import { CompanyAvatarCluster } from './components/CompanyAvatarCluster'

const STEP_AGENDA = 0
const STEP_DATA = 1
const STEP_REPORT = 2
const STEP_GENERATE_OUTPUTS = 3

function getInitialStep(status?: string | null): number {
  const s = String(status || '').toUpperCase()
  if (s === AdvisoryCallStatus.PREPARE_EXPORTING || s === AdvisoryCallStatus.EXPORTING) return STEP_GENERATE_OUTPUTS
  // Only a brand-new (not-yet-analysed) report starts on the agenda.
  if (s === '' || s === AdvisoryCallStatus.PENDING) return STEP_AGENDA
  // Analysed / under review / chat-in-progress / errored / exported → the review step.
  return STEP_REPORT
}

const CONSOLIDATED_LIST = '/adviceprep/consolidated'

export default function AdvisoryConsolidatedPage() {
  const { groupId: callId = '' } = useParams<{ groupId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const clientId = useAuthSession()?.currentClientId || ''

  const { group, isLoading: groupLoading } = useConsolidatedAnalysis(callId, clientId)
  const reportQuery = useAdvisoryAiReport(callId, clientId, true)

  const { addToast } = useToast()
  const [currentStep, setCurrentStep] = useState(STEP_AGENDA)
  const [pendingExportsRefresh, setPendingExportsRefresh] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const initialStepSet = useRef(false)

  const updateTitleMutation = useUpdateAdvisoryCallMutation(callId, clientId)

  const meetingTitle = reportQuery.data?.meetingTitle || 'Consolidated Analysis'

  const startEditTitle = () => {
    setDraftTitle(reportQuery.data?.meetingTitle || '')
    setIsEditingTitle(true)
  }

  const handleSaveTitle = async () => {
    const next = draftTitle.trim()
    if (!next) {
      addToast('Title cannot be empty', 'error')
      return
    }
    try {
      await updateTitleMutation.mutateAsync({ meetingTitle: next })
      setIsEditingTitle(false)
      addToast('Title updated', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to update title', 'error')
    }
  }

  const requestedStep = Number(searchParams.get('step'))
  const requestedStepIndex =
    Number.isInteger(requestedStep) && requestedStep >= 1 && requestedStep <= 4 ? requestedStep - 1 : null

  useEffect(() => {
    initialStepSet.current = false
  }, [callId])

  useEffect(() => {
    if (initialStepSet.current || !reportQuery.data?.status) return
    initialStepSet.current = true
    setCurrentStep(requestedStepIndex !== null ? requestedStepIndex : getInitialStep(reportQuery.data.status))
  }, [callId, reportQuery.data?.status, requestedStepIndex])

  const steps = [
    { title: 'Agenda' },
    { title: 'Data' },
    { title: 'AI Analysis & Review' },
    { title: 'Generate Outputs' },
  ]

  if (!clientId) {
    return <Alert type="warning" showIcon message="No active client selected" />
  }

  if ((groupLoading || reportQuery.isLoading) && !group) {
    return (
      <FeatureThemeProvider featureKey="advisoryIntel" withAntd className="flex items-center justify-center h-64">
        <Spin size="large" />
      </FeatureThemeProvider>
    )
  }

  if (!group) {
    return (
      <div className="space-y-4">
        <Alert type="warning" showIcon message="Consolidated analysis not found" />
        <Button icon={<LeftOutlined />} onClick={() => navigate(CONSOLIDATED_LIST)}>
          Back to analyses
        </Button>
      </div>
    )
  }

  const meetingDateLabel = reportQuery.data?.meetingDate
    ? dayjs(reportQuery.data.meetingDate).format('dddd D MMM YYYY - h:mm A')
    : `${group.orgs.length} companies`

  return (
    <FeatureThemeProvider
      featureKey="advisoryIntel"
      withAntd
      className="w-full max-w-6xl mx-auto"
    >
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <CompanyAvatarCluster names={group.orgs.map((o) => o.orgName)} sizeClass="w-12 h-12" textClass="text-lg" />
            <div>
              {isEditingTitle ? (
                <Space.Compact className="w-full max-w-md">
                  <Input
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    onPressEnter={() => void handleSaveTitle()}
                    maxLength={120}
                    size="large"
                    autoFocus
                    disabled={updateTitleMutation.isPending}
                  />
                  <Button
                    type="primary"
                    size="large"
                    className="feature-button-solid"
                    loading={updateTitleMutation.isPending}
                    onClick={() => void handleSaveTitle()}
                  >
                    Save
                  </Button>
                  <Button size="large" onClick={() => setIsEditingTitle(false)} disabled={updateTitleMutation.isPending}>
                    Cancel
                  </Button>
                </Space.Compact>
              ) : (
                <div className="flex items-center gap-1">
                  <h1 className="text-2xl font-bold text-gray-900">{meetingTitle}</h1>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    aria-label="Edit title"
                    className="text-gray-400 feature-link"
                    onClick={startEditTitle}
                  />
                </div>
              )}
              <p className="text-sm text-gray-600">
                {group.orgs.length} companies · {meetingDateLabel}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button icon={<LeftOutlined />} onClick={() => navigate(CONSOLIDATED_LIST)}>
              All analyses
            </Button>
          </div>
        </div>

        <div className="mb-2" data-tour="stepper">
          <Steps current={currentStep} items={steps.map((s) => ({ title: s.title }))} />
        </div>
      </div>

      <div data-tour="step-body" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        {currentStep === STEP_AGENDA && (
          <AgendaStep
            callId={callId}
            clientId={clientId}
            orgId={group.orgs[0]?.orgId ?? ''}
            onContinue={() => setCurrentStep(STEP_DATA)}
          />
        )}
        {currentStep === STEP_DATA && (
          <DataStep
            orgId=""
            callId={callId}
            clientId={clientId}
            orgName={reportQuery.data?.meetingTitle ?? ''}
            onBack={() => setCurrentStep(STEP_AGENDA)}
            onContinue={() => setCurrentStep(STEP_REPORT)}
          />
        )}
        {currentStep === STEP_REPORT && (
          <ReportStep
            callId={callId}
            clientId={clientId}
            executiveSummaryAsAgenda
            onBack={() => setCurrentStep(STEP_DATA)}
            onFinish={() => setCurrentStep(STEP_GENERATE_OUTPUTS)}
          />
        )}
        {currentStep === STEP_GENERATE_OUTPUTS && (
          <GenerateOutputsStep
            orgId=""
            callId={callId}
            clientId={clientId}
            onBack={() => setCurrentStep(STEP_REPORT)}
            pendingExportsRefresh={pendingExportsRefresh}
            onExportsRefreshed={() => setPendingExportsRefresh(false)}
            listHref={CONSOLIDATED_LIST}
            listLabel="Back to Analyses"
          />
        )}
      </div>

    </FeatureThemeProvider>
  )
}
